// The React renderer for the UiNode contract. Generated by `sui generate` from .sui sources; do not edit.

import type { FormEvent } from "react";
import {
  Activity,
  BarChart3,
  Blocks,
  BookOpen,
  Bot,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  CircleHelp,
  Cloud,
  Code2,
  Contact,
  CreditCard,
  FileSpreadsheet,
  FolderKanban,
  Globe2,
  House,
  LayoutDashboard,
  Link2,
  Mail,
  MessageSquare,
  PackageOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  ReceiptText,
  Scale,
  ScanBarcode,
  Settings,
  ShoppingCart,
  Sparkles,
  Unlink,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon, LucideProps } from "lucide-react";
import type {
  ButtonNode,
  ButtonVariant,
  CardNode,
  Columns,
  Direction,
  FormNode,
  Gap,
  GridNode,
  HeroNode,
  Icon,
  LinkNode,
  StackNode,
  TableNode,
  TextNode,
  TextStyle,
  UiNode,
} from "./types.js";

export interface CommandInvocation {
  readonly command: string;
  readonly values?: Readonly<Record<string, FormDataEntryValue>>;
}

export interface SuiRendererProps {
  readonly node: UiNode;
  readonly onCommand: (invocation: CommandInvocation) => void;
}

type OnCommand = SuiRendererProps["onCommand"];

function cx(...parts: ReadonlyArray<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

const textClass: Readonly<Record<TextStyle, string>> = {
  heading: "text-2xl font-semibold tracking-tight",
  body: "text-sm leading-6",
  muted: "text-sm text-[var(--muted-foreground)]",
  label: "text-xs font-semibold uppercase tracking-wider",
};

const variantClass: Readonly<Record<ButtonVariant, string>> = {
  primary: "bg-[var(--action)] text-[var(--action-foreground)] shadow-sm hover:brightness-110",
  secondary: "bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--muted-strong)]",
  outline: "border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--muted)]",
  ghost: "hover:bg-[var(--muted)]",
};

const directionClass: Readonly<Record<Direction, string>> = {
  row: "flex-row flex-wrap items-center",
  column: "flex-col",
};

const gapClass: Readonly<Record<Gap, string>> = {
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
};

const columnsClass: Readonly<Record<Columns, string>> = {
  1: "grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-2 xl:grid-cols-4",
};

const iconComponent: Readonly<Record<Icon, LucideIcon>> = {
  activity: Activity,
  "bar-chart": BarChart3,
  blocks: Blocks,
  book: BookOpen,
  bot: Bot,
  briefcase: BriefcaseBusiness,
  building: Building2,
  calendar: CalendarDays,
  chart: ChartNoAxesCombined,
  cloud: Cloud,
  code: Code2,
  contact: Contact,
  "credit-card": CreditCard,
  "file-spreadsheet": FileSpreadsheet,
  folder: FolderKanban,
  globe: Globe2,
  help: CircleHelp,
  home: House,
  "layout-dashboard": LayoutDashboard,
  "link-2": Link2,
  mail: Mail,
  message: MessageSquare,
  package: PackageOpen,
  "panel-left-close": PanelLeftClose,
  "panel-left-open": PanelLeftOpen,
  plus: Plus,
  receipt: ReceiptText,
  scale: Scale,
  "scan-barcode": ScanBarcode,
  settings: Settings,
  "shopping-cart": ShoppingCart,
  sparkles: Sparkles,
  unlink: Unlink,
  users: Users,
  x: X,
};

function IconComponent({ kind, ...props }: { kind: Icon } & LucideProps) {
  const Component = iconComponent[kind];
  return <Component {...props} />;
}

function TextView({ node }: { node: TextNode }) {
  return <p className={textClass[node.style ?? "body"]}>{node.text}</p>;
}

function ButtonView({ node, onCommand }: { node: ButtonNode; onCommand: OnCommand }) {
  return (
    <button
      className={cx("inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] disabled:pointer-events-none disabled:opacity-45", variantClass[node.variant ?? "primary"], "h-10 px-4 text-sm")}
      onClick={() => onCommand({ command: node.command })}
    >
      {node.icon ? <IconComponent kind={node.icon} className="size-4" /> : null}
      {node.label}
    </button>
  );
}

function LinkView({ node }: { node: LinkNode }) {
  return (
    <a
      className="inline-flex items-center gap-2 font-medium text-[var(--action)] hover:underline"
      href={node.href}
    >
      {node.icon ? <IconComponent kind={node.icon} className="size-4" /> : null}
      {node.label}
    </a>
  );
}

function StackView({ node, onCommand }: { node: StackNode; onCommand: OnCommand }) {
  return (
    <div className={cx("flex", directionClass[node.direction ?? "column"], gapClass[node.gap ?? "md"])}>
      {node.children.map((child, index) => (
        <SuiRenderer key={index} node={child} onCommand={onCommand} />
      ))}
    </div>
  );
}

function GridView({ node, onCommand }: { node: GridNode; onCommand: OnCommand }) {
  return (
    <div className={cx("grid gap-4", columnsClass[node.columns ?? 2])}>
      {node.children.map((child, index) => (
        <SuiRenderer key={index} node={child} onCommand={onCommand} />
      ))}
    </div>
  );
}

function CardView({ node, onCommand }: { node: CardNode; onCommand: OnCommand }) {
  return (
    <section className="surface rounded-2xl">
      {node.title || node.description ? (
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div>
            {node.title ? <h2 className="text-sm font-semibold tracking-tight">{node.title}</h2> : null}
            {node.description ? (
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">{node.description}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="p-5 grid gap-4">
        {node.children.map((child, index) => (
          <SuiRenderer key={index} node={child} onCommand={onCommand} />
        ))}
      </div>
    </section>
  );
}

function HeroView({ node, onCommand }: { node: HeroNode; onCommand: OnCommand }) {
  return (
    <section className="surface overflow-hidden rounded-3xl p-8 sm:p-12">
      <div className="max-w-3xl">
        {node.eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--action)]">
            {node.eyebrow}
          </p>
        ) : null}
        <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-6xl">{node.title}</h1>
        {node.description ? (
          <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--muted-foreground)] sm:text-lg">
            {node.description}
          </p>
        ) : null}
        {node.actions ? (
          <div className="mt-7 flex flex-wrap gap-3">
            {node.actions?.map((action, index) => (
              <SuiRenderer key={index} node={action} onCommand={onCommand} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function FormView({ node, onCommand }: { node: FormNode; onCommand: OnCommand }) {
  return (
    <form
      className="grid gap-4"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onCommand({ command: node.command, values: Object.fromEntries(new FormData(event.currentTarget)) });
      }}
    >
      {node.fields.map((field) => (
        <label key={field.name} className="grid gap-1.5 text-sm" htmlFor={`${node.id}-${field.name}`}>
          <span>{field.label}</span>
          {field.input === "textarea" ? (
            <textarea
              className="input min-h-28 resize-y"
              id={`${node.id}-${field.name}`}
              name={field.name}
              placeholder={field.placeholder}
              required={field.required}
            />
          ) : (
            <input
              className="input"
              id={`${node.id}-${field.name}`}
              name={field.name}
              type={field.input}
              placeholder={field.placeholder}
              required={field.required}
            />
          )}
        </label>
      ))}
      <button
        className={cx("inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] disabled:pointer-events-none disabled:opacity-45", variantClass["primary"], "h-10 px-4 text-sm")}
        type="submit"
      >
        {node.submitLabel}
      </button>
    </form>
  );
}

function TableView({ node }: { node: TableNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--border)]">
            {node.columns.map((column) => (
              <th key={column.key} className="px-3 py-2 text-xs font-medium text-[var(--muted-foreground)]">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {node.rows.map((row, index) => (
            <tr key={index} className="border-b border-[var(--border)]/60">
              {node.columns.map((column) => (
                <td key={column.key} className="px-3 py-2">{String(row[column.key] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SuiRenderer({ node, onCommand }: SuiRendererProps) {
  switch (node.type) {
    case "text":
      return <TextView node={node} />;
    case "button":
      return <ButtonView node={node} onCommand={onCommand} />;
    case "link":
      return <LinkView node={node} />;
    case "stack":
      return <StackView node={node} onCommand={onCommand} />;
    case "grid":
      return <GridView node={node} onCommand={onCommand} />;
    case "card":
      return <CardView node={node} onCommand={onCommand} />;
    case "hero":
      return <HeroView node={node} onCommand={onCommand} />;
    case "form":
      return <FormView node={node} onCommand={onCommand} />;
    case "table":
      return <TableView node={node} />;
    default:
      return null;
  }
}
